'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { stripHtml, useLoyaltyContext, usePageLayout } from '@libs/common-next';
import { getLocale, logger } from '@libs/core';

import { dataLayerPush } from '../../gtm';
import { trackAccountPage } from '../../helpers';
import type { EventTracking } from '../../types';

export type ContentSquareTrackingProps = {
    pageId: string;
    isSearchPage?: boolean;
    entryId?: string;
};

export const trackingData = (data: { [key: string]: unknown }) => data;

const getEventData = ({
    isAuthenticated,
    points,
    pageId,
    subSection,
    isSearchPage,
    entryId,
    pathname,
    currentQuery,
    customerKey,
}: {
    isAuthenticated: string | undefined;
    points: number | undefined;
    pageId: string;
    subSection: string;
    isSearchPage: boolean;
    entryId: string | undefined;
    pathname: string | null;
    currentQuery: string;
    customerKey: number | undefined;
}): EventTracking => {
    const loginState = Boolean(isAuthenticated);
    const baseEventData: EventTracking = {
        language: getLocale(),
        loginState,
        totalTreatsPoints: points,
        pageName: `ps:${pageId}`,
        section: pageId,
        subSection,
        customerKey,
    };

    switch (pageId) {
        case 'PLP':
            return {
                ...baseEventData,
                event: 'site-search-triggered',
                pageName: isSearchPage ? `ps:search-result:${currentQuery}` : `ps:blip:${entryId}`,
                'search-result-path': window.location.href,
                'search-term': currentQuery,
                section: isSearchPage ? 'PLP Search' : 'PLP Browse',
            };
        case 'ACP':
            return trackAccountPage(pathname, baseEventData);
        default:
            return baseEventData;
    }
};

export function ContentSquareTracking({ pageId, isSearchPage = false, entryId }: ContentSquareTrackingProps) {
    const { isAuthenticated, user } = usePageLayout();
    const { memberPoints } = useLoyaltyContext();

    const params = useSearchParams();
    const pathname = usePathname();
    const currentQuery = stripHtml(params?.get('q') ?? '');
    const subSection = params?.get('gtmSubSection') ?? '';
    const customerKey = user?.customerprofileid;

    const points = memberPoints?.availablePoints ?? 0;

    useEffect(() => {
        logger.info(`ContentSquareTracking Loaded pageId="${pageId}" isSearchPage="${isSearchPage}" `);
    }, []);

    useEffect(() => {
        dataLayerPush(
            trackingData(
                getEventData({
                    isAuthenticated,
                    points,
                    pageId,
                    subSection,
                    isSearchPage,
                    entryId,
                    pathname,
                    currentQuery,
                    customerKey,
                })
            )
        );
    }, [currentQuery, pageId, isSearchPage, subSection, entryId, isAuthenticated, points, pathname]);

    return false;
}
